import React, { useEffect, useState, useContext } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { AuthContext } from "../context/AuthContext";
import api from "../services/api";
import { userRoles } from "../App";
import "../assets/styles/RequestsManager.css"; // تأكد من المسار

const RequestsManager = () => {
  const { user } = useContext(AuthContext);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [reason, setReason] = useState("");
  const [requestType, setRequestType] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const res = await api.get("/users/show-my-role-requests");      
      setRequests(res.data.data);
    } catch (err) {
      console.error("Error fetching requests:", err);
    } finally {
      setLoading(false);
    }
  };

  const hasPendingRequest = requests.some((r) => r.status === "pending");

  const handleOpenModal = (type) => {
    setRequestType(type);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (reason.length < 10) {
      alert("Reason must be at least 10 characters.");
      return;
    }
    setSubmitLoading(true);
    try {
      const endpoint = requestType === "upgrade" ? "/users/request-upgrade" : "/users/request-downgrade";
      await api.post(endpoint, { reason });
      await fetchRequests();
      setShowModal(false);
      setReason("");
    } catch (err) {
      console.error("Error submitting request:", err);
      alert(err.response?.data?.message || "Something went wrong");
    } finally {
      setSubmitLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: "In Review", class: "status-pending", icon: "bi-hourglass-split" },
      approved: { label: "Approved", class: "status-approved", icon: "bi-check-circle-fill" },
      rejected: { label: "Rejected", class: "status-rejected", icon: "bi-x-circle-fill" },
    };
    const current = statusMap[status] || { label: status, class: "status-default", icon: "bi-info-circle" };

    return (
      <span className={`app-badge ${current.class}`}>
        <i className={`bi ${current.icon} me-1`}></i> {current.label}
      </span>
    );
  };

  if (loading) return (
    <div className="vh-100 d-flex justify-content-center align-items-center bg-light">
      <div className="spinner-border text-primary" style={{width: '3rem', height: '3rem'}} role="status"></div>
    </div>
  );

  return (
    <div className="app-wrapper" style={{ marginTop: "90px", paddingBottom: "100px" }}>
      <div className="container px-3">
        
        {/* 1. App Header */}
        <div className="app-header mb-4 mt-4">
          <div>
            <h5 className="text-secondary small text-uppercase ls-1 mb-1 fw-bold">Role Management</h5>
            <h1 className="fw-bold text-dark m-0 display-6">My Requests</h1>
          </div>
          <div className="header-icon bg-white text-primary shadow-sm">
            <i className="bi bi-person-gear"></i>
          </div>
        </div>

        {/* 2. Main Action Card (Hero Section) */}
        <div className="row justify-content-center mb-4">
          <div className="col-lg-6 col-md-8 col-12">
            <div className={`app-card hero-card ${user?.role === userRoles.student ? 'gradient-blue' : 'gradient-orange'}`}>
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div className="text-white">
                  <h4 className="fw-bold mb-1 text-white">
                    {user?.role === userRoles.student ? "Become a Supervisor" : "Back to Student"}
                  </h4>
                  <p className="opacity-75 small m-0 text-white" style={{maxWidth: '250px'}}>
                    {user?.role === userRoles.student 
                      ? "Upgrade your account to manage activities and lead teams." 
                      : "Resign from your position and return to student privileges."}
                  </p>
                </div>
                <div className="hero-icon">
                  <i className={`bi ${user?.role === userRoles.student ? 'bi-shield-check' : 'bi-mortarboard'}`}></i>
                </div>
              </div>

              {user?.role === userRoles.student ? (
                <button
                  className="btn app-btn-white text-secondary w-100"
                  onClick={() => handleOpenModal("upgrade")}
                  disabled={hasPendingRequest}
                >
                  {hasPendingRequest ? "Application In Review..." : "Apply Now"}
                </button>
              ) : (
                <button
                  className="btn app-btn-white w-100"
                  onClick={() => handleOpenModal("downgrade")}
                  disabled={hasPendingRequest}
                >
                   {hasPendingRequest ? "Request In Review..." : "Request Resignation"}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 3. History Section */}
        <div className="row justify-content-center">
          <div className="col-lg-6 col-md-8 col-12">
            <div className="d-flex justify-content-between align-items-center mb-3 px-1">
              <h6 className="fw-bold text-dark m-0">Recent History</h6>
              <span className="bg-white text-secondary border rounded-pill px-2">{requests.length}</span>
            </div>
            
            {requests.length > 0 ? (
              <div className="d-flex flex-column gap-3">
                {requests.map((req) => (
                  <div key={req._id} className="app-card history-card">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div className="d-flex align-items-center gap-3">
                        <div className={`history-icon ${req.type === 'upgrade' ? 'icon-up' : 'icon-down'}`}>
                           {req.type === 'upgrade' ? <i className="bi bi-arrow-up-right"></i> : <i className="bi bi-arrow-down-left"></i>}
                        </div>
                        <div>
                          <h6 className="fw-bold text-dark m-0 text-capitalize">{req.type} Request</h6>
                          <small className="text-muted fw-bold" style={{fontSize: '0.75rem'}}>
                              {new Date(req.createdAt).toLocaleDateString()}
                          </small>
                        </div>
                      </div>
                      {getStatusBadge(req.status)}
                    </div>
                    
                    <div className="bg-light rounded-3 p-3 mt-2 border border-light">
                       <p className="text-dark small m-0 fst-italic">"{req.reason}"</p>
                    </div>

                    {req.adminNote && (
                      <div className="admin-note mt-2">
                        <i className="bi bi-chat-quote-fill me-2"></i>
                        <span className="small fw-bold text-dark">{req.adminNote}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-icon"><i className="bi bi-inbox"></i></div>
                <p className="text-muted fw-bold">No history found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 4. Center Modal (Mobile & Desktop) */}
      {showModal && (
        <div className="modal-backdrop-blur" onClick={() => setShowModal(false)}>
          <div className="modal-sheet" onClick={(e) => e.stopPropagation()}>
            
            <div className="sheet-header">
              <h5 className="fw-bold m-0 text-dark">
                {requestType === 'upgrade' ? 'Apply for Upgrade' : 'Resign Position'}
              </h5>
              <button className="btn-close" onClick={() => setShowModal(false)}></button>
            </div>
            
            <form onSubmit={handleSubmit} className="sheet-body">
              <div className="mb-3">
                <label className="form-label fw-bold small text-uppercase text-muted">Reason for request</label>
                <textarea
                  className="form-control app-input"
                  rows="5"
                  placeholder={requestType === 'upgrade' 
                    ? "Tell us why you'd be a great supervisor..." 
                    : "Please share why you want to downgrade..."}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  required
                  minLength={10}
                ></textarea>
                <div className="text-end mt-1">
                    <small className={reason.length < 10 ? "text-danger fw-bold" : "text-success fw-bold"}>
                        {reason.length} / 10 characters
                    </small>
                </div>
              </div>
              <button type="submit" className="btn btn-dark w-100 py-3 rounded-pill fw-bold shadow-lg" disabled={submitLoading || reason.length < 10}>
                {submitLoading ? "Submitting..." : "Submit Application"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestsManager;